import { HttpInterceptorFn, HttpErrorResponse, HttpBackend, HttpClient } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, throwError, switchMap, BehaviorSubject, filter, take } from 'rxjs';
import { ToastService } from '../services/toast.service';
import { environment } from '../../../environments/environment';

/** Returns true only if the value is a non-empty, non-placeholder string. */
function isValidToken(value: string | null): value is string {
  if (!value || value === 'undefined' || value === 'null') return false;
  return value.trim().length > 0;
}

// Module-level refresh state — prevents multiple simultaneous refresh calls
let isRefreshing = false;
const refreshDone$ = new BehaviorSubject<boolean>(false);

function clearSession(router: Router, toast: ToastService) {
  localStorage.removeItem('sw_token');
  localStorage.removeItem('sw_refresh');
  localStorage.removeItem('sw_user');
  toast.warning('Your session has expired. Please log in again.');
  router.navigate(['/auth/login']);
}

export const tokenInterceptor: HttpInterceptorFn = (req, next) => {
  const router = inject(Router);
  const toast = inject(ToastService);
  const backend = inject(HttpBackend);
  const raw = localStorage.getItem('sw_token');
  const token = isValidToken(raw) ? raw.trim() : null;

  // Only skip the token for endpoints that don't require auth (login, refresh, register)
  const unauthenticated = ['/auth/login', '/auth/refresh', '/auth/register'];
  const isUnauthEndpoint = unauthenticated.some(path => req.url.includes(path));
  const authReq = (token && !isUnauthEndpoint)
    ? req.clone({ setHeaders: { Authorization: `Bearer ${token}` } })
    : req;

  return next(authReq).pipe(
    catchError((err: HttpErrorResponse) => {
      if (err.status === 401 && !isUnauthEndpoint) {
        const refreshToken = localStorage.getItem('sw_refresh');

        if (!isValidToken(refreshToken)) {
          clearSession(router, toast);
          return throwError(() => err);
        }

        if (!isRefreshing) {
          isRefreshing = true;
          refreshDone$.next(false);

          // Use HttpBackend directly to bypass this interceptor and avoid circular calls
          const bypassHttp = new HttpClient(backend);
          return bypassHttp.post<any>(`${environment.apiUrl}/auth/refresh`, { refreshToken }).pipe(
            switchMap(res => {
              const newToken: string = res.token ?? res.accessToken ?? res.access_token ?? '';
              const newRefresh: string = res.refreshToken ?? res.refresh_token ?? '';
              if (newToken) localStorage.setItem('sw_token', newToken.trim());
              if (newRefresh) localStorage.setItem('sw_refresh', newRefresh.trim());
              isRefreshing = false;
              refreshDone$.next(true);
              const retried = req.clone({ setHeaders: { Authorization: `Bearer ${newToken.trim()}` } });
              return next(retried);
            }),
            catchError(refreshErr => {
              isRefreshing = false;
              refreshDone$.next(false);
              clearSession(router, toast);
              return throwError(() => refreshErr);
            })
          );
        } else {
          // Another request already triggered a refresh — wait for it to finish then retry
          return refreshDone$.pipe(
            filter(done => done),
            take(1),
            switchMap(() => {
              const retryToken = localStorage.getItem('sw_token');
              const retried = isValidToken(retryToken)
                ? req.clone({ setHeaders: { Authorization: `Bearer ${retryToken!.trim()}` } })
                : req;
              return next(retried);
            })
          );
        }
      }
      return throwError(() => err);
    })
  );
};
